import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import SecondaryButton from '../common/SecondaryButton';
import useAppURLs from '../../common/useAppURLs';
import { getAPIURL } from '../../common/utils';

// Webflow section_cta25: centered "Get started with X" block. Buttons carry
// either a static href or an `app` marker resolved to the environment's app
// URL (auth-aware) after mount.
//
// `app: 'swagger'` (getAPIURL()) reads window.API_URL — only ever set by
// start-prod.sh's runtime env-config.js in a real browser, never during
// SSR/prerender — so resolving it at render time bakes its process.env
// fallback ('http://127.0.0.1:8000' when unset) straight into prerendered
// HTML (OpenConceptLab/ocl_online#125 point 5). Resolving it in an effect
// instead keeps that fallback out of the static output. The other app
// markers (termBrowser/mapper) read localStorage, so they're resolved after
// mount too (common/useAppURLs.js), and link the production apps until then.
//
// The API root itself only serves a JSON index, so the "browse the live API"
// button points at the service's browsable Swagger UI instead.
const GetStartedCta = ({ title, text, buttons }) => {
  const appURLs = useAppURLs();
  const [swaggerURL, setSwaggerURL] = React.useState(null);
  React.useEffect(() => { setSwaggerURL(`${getAPIURL()}/swagger/`); }, []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
      <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'surface.contrastText', mb: 4 }}>
        {text}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
        {buttons.map(button => {
          const href = button.app === 'swagger' ? swaggerURL : (button.app ? appURLs[button.app] : button.href);
          return button.primary ? (
            <Button key={button.label} href={href} variant="contained" disableElevation>
              {button.label}
            </Button>
          ) : (
            <SecondaryButton key={button.label} href={href}>
              {button.label}
            </SecondaryButton>
          );
        })}
      </Box>
    </Container>
  );
};

export default GetStartedCta;
